import { Button, SvgIcon } from '@mui/material';
import { RiAddLine } from '@remixicon/react';
import { Link } from 'react-router';
import { MainSectionTemplate } from '../../../components/AdminTemplates';
import CustomCalendar from '../../../components/CustomCalendar/CustomCalendar';
import { useCurrentUser } from '../../../contexts/currentUser';
import { useLanguage } from '../../../contexts/language';
import useCustomCalendarManager, {
  CustomCalendarManagerInitialState,
} from '../../../hooks/useCustomCalendarManager';
import {
  AllowedCalendarRangeTypes,
  AllowedCalendarView,
  EntityStatus,
  JourneyStatus,
} from '../../../types/core';
import useSearchParamsFilters, {
  FilterDefinition,
} from '../../../hooks/useSearchParamsFilters';
import { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { DAYJS_DATA_DATE_FORMAT } from '../../../utils/date';

const CONTRACT_FIELD = 'contractId';
const DATE_FIELD = 'date';
const VIEW_FIELD = 'view';
const ENTITY_STATUS_FIELD = 'entityStatus';
const JOURNEY_STATUS_FIELD = 'journeyStatus';

const JourneysPage = () => {
  const { t } = useLanguage();
  const { user } = useCurrentUser();

  const [filters, setFilters] = useSearchParamsFilters();
  const calendarInitialState =
    useMemo<CustomCalendarManagerInitialState>(() => {
      const initialState: CustomCalendarManagerInitialState = {};
      if (!filters.length) {
        return initialState;
      }

      const selectedContractId =
        (filters.find(
          filter =>
            filter.field === CONTRACT_FIELD && typeof filter.value === 'string',
        )?.value as string) ?? null;
      const selectedDate = dayjs(
        (filters.find(
          filter =>
            filter.field === DATE_FIELD && typeof filter.value === 'string',
        )?.value as string) ?? 'invalid date',
      );
      const selectedView =
        (filters.find(
          filter =>
            filter.field === VIEW_FIELD &&
            Object.values(AllowedCalendarView).includes(
              filter.value as AllowedCalendarView,
            ),
        )?.value as AllowedCalendarView) ?? null;

      if (selectedContractId || selectedDate.isValid() || selectedView) {
        initialState.calendarState = {
          selectedContractId,
          selectedDate: selectedDate.isValid() ? selectedDate : undefined,
          selectedView,
        };
      }

      initialState.entityStatusFilters = filters.find(
        filter =>
          filter.field === ENTITY_STATUS_FIELD && Array.isArray(filter.value),
      )?.value as EntityStatus[];

      initialState.journeyStatusFilters = filters.find(
        filter =>
          filter.field === JOURNEY_STATUS_FIELD && Array.isArray(filter.value),
      )?.value as JourneyStatus[];

      return initialState;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

  const { isLoading, customCalendarProps } = useCustomCalendarManager(
    `journeys-${user.id}`,
    AllowedCalendarRangeTypes.Monthly,
    calendarInitialState,
  );

  useEffect(() => {
    const filters: FilterDefinition[] = [];
    if (customCalendarProps.selectedContractId) {
      filters.push({
        field: CONTRACT_FIELD,
        operator: '',
        value: customCalendarProps.selectedContractId,
      });
    }
    filters.push({
      field: DATE_FIELD,
      operator: '',
      value: customCalendarProps.selectedDate.format(DAYJS_DATA_DATE_FORMAT),
    });
    filters.push({
      field: VIEW_FIELD,
      operator: '',
      value: customCalendarProps.selectedView,
    });

    if (customCalendarProps.entityStatusFilters.length) {
      filters.push({
        field: ENTITY_STATUS_FIELD,
        operator: '',
        value: customCalendarProps.entityStatusFilters,
      });
    }

    if (customCalendarProps.journeyStatusFilters.length) {
      filters.push({
        field: JOURNEY_STATUS_FIELD,
        operator: '',
        value: customCalendarProps.journeyStatusFilters,
      });
    }

    setFilters(filters);
  }, [
    customCalendarProps.selectedContractId,
    customCalendarProps.selectedDate,
    customCalendarProps.selectedView,
    customCalendarProps.entityStatusFilters,
    customCalendarProps.journeyStatusFilters,
    setFilters,
  ]);

  return (
    <MainSectionTemplate
      title={t('content.journeys')}
      isLoading={isLoading}
      actions={
        <Link to="/admin/journeys/create">
          <Button
            startIcon={<SvgIcon component={RiAddLine} fontSize="small" />}
            size="small"
            variant="contained"
          >
            {t('content.add-new')}
          </Button>
        </Link>
      }
    >
      <CustomCalendar {...customCalendarProps} />
    </MainSectionTemplate>
  );
};

export default JourneysPage;
